import { useMemo } from 'react';
import { svgToDataUri } from '../../../utils/styles/svgMixins';

/**
 * Custom hook that encapsulates all business logic for AdditionalPDPLabel
 * @param {Object} props - Component props
 * @returns {Object} - Processed data and computed values
 */
export const useAdditionalPDPLabel = ({ label, type, productType, desktop }) => {
  // Extract label properties with destructuring and default values
  const { title, description, moreInfoLink, moreInfoDisplayText, icon } = label ?? {};

  // Determine if component should render based on content availability
  const shouldRender = useMemo(() => {
    return Boolean(description || title || moreInfoLink);
  }, [description, title, moreInfoLink]);

  const processedDescription = useMemo(() => {
    if (!description) return null;

    // Replace <strong tags with styled version using native replace
    return description.replace(/<strong/gi, "<strong style='font-weight: bold'");
  }, [description]);

  // Generate CSS classes using modern conditional logic
  const containerClasses = useMemo(() => {
    const classes = ['additional-label-in-pdp'];

    if (type === 'warranty') {
      classes.push('warranty');
    }

    if (productType === 'softline' && !desktop) {
      classes.push('softline');
    }

    return classes;
  }, [type, productType, desktop]);

  // Compute link display text with fallback
  const linkDisplayText = useMemo(() => {
    return moreInfoDisplayText || 'More Information';
  }, [moreInfoDisplayText]);

  // Generate icon style object with svgToDataUri processing
  const iconStyle = useMemo(() => {
    if (!icon) return {};

    return {
      backgroundImage: `url('${svgToDataUri(icon)}')`,
    };
  }, [icon]);

  // Computed values for rendering logic
  const computedValues = useMemo(
    () => ({
      hasTitle: Boolean(title),
      hasDescription: Boolean(description),
      hasMoreInfoLink: Boolean(moreInfoLink),
      hasIcon: Boolean(icon),
    }),
    [title, description, moreInfoLink, icon]
  );

  return {
    // Processed data
    title,
    processedDescription,
    moreInfoLink,
    linkDisplayText,
    iconStyle,

    // CSS classes
    containerClasses,

    // Computed values
    shouldRender,
    ...computedValues,
  };
};
